'use client'

import { useTranslations } from 'next-intl'
import type { PaginationProps } from '@/app/[locale]/_components/Pagination/types'
import AppLink from '@/components/elementary/AppLink'
import { ArrowLeft } from '@/components/icons/ArrowLeft'
import { ArrowRight } from '@/components/icons/ArrowRight'
import { cn } from '@/lib/styles'
import { PAGINATION_TEST_IDS } from '@/lib/test-ids'

// this is a client component because we want to be able to use it on both ends, but it cannot use any client-side logic
// adding client-side logic will disable server-side pre-rendering, thus disabling crawling for paginated pages
const PaginationRenderer = ({
	totalPages,
	currentPage = 1,
	useSearchParamPagination,
	pathname,
	paramsObject,
	pagination,
}: PaginationProps) => {
	const t = useTranslations('buttons')

	const getPagePath = (pathname: string, page: number) => {
		const path = page === 1 ? pathname : [pathname, 'page', page].join('/')

		// this is a quick and cheap fix to remove duplicate / symbols from the path
		return path.replace(/\/{2,}/g, '/')
	}

	return (
		<div
			data-test-id={PAGINATION_TEST_IDS.COMPONENT}
			className="w-full flex justify-center md:justify-between items-center py-8 mt-12 border-t border-neutral-light-500"
		>
			{totalPages > 1 && (
				<>
					<div className="w-19 justify-start hidden items-center gap-2 text-2xl md:flex">
						{currentPage > 1 && (
							<AppLink
								href={
									useSearchParamPagination
										? {
												pathname,
												query: {
													...paramsObject,
													...(currentPage > 2 ? { page: currentPage - 1 } : {}),
												},
											}
										: getPagePath(pathname, currentPage - 1)
								}
								variant="tertiaryLink"
								className="no-underline font-semibold"
								data-test-id={PAGINATION_TEST_IDS.PAGINATION_PREVIOUS}
								iconLeft={<ArrowLeft className="size-5" />}
							>
								{t('prev')}
							</AppLink>
						)}
					</div>
					<div className="flex items-end">
						{(pagination ?? []).map((page) => (
							<div key={page}>
								{page === '...' ? (
									<span className="mx-1 text-2xl text-neutral-500">{page}</span>
								) : (
									<AppLink
										href={
											useSearchParamPagination
												? {
														pathname,
														query: {
															...paramsObject,
															...(Number(page) > 1
																? {
																		page: page,
																	}
																: {}),
														},
													}
												: getPagePath(pathname, Number(page))
										}
										size="none"
										className="no-underline justify-center items-center"
										variant="tertiaryLink"
										data-test-id={PAGINATION_TEST_IDS.PAGINATION_PAGE}
									>
										<span
											className={cn(
												'size-8 flex items-center justify-center border border-transparent rounded-[5px] text-xl font-bold',
												currentPage === page && 'border-peach-600 text-peach-600',
											)}
										>
											{page}
										</span>
									</AppLink>
								)}
							</div>
						))}
					</div>
					<div className="w-19 justify-end hidden items-center gap-2 text-2xl  md:flex">
						{currentPage < totalPages && (
							<AppLink
								href={
									useSearchParamPagination
										? {
												pathname,
												query: {
													...paramsObject,
													page: currentPage + 1,
												},
											}
										: getPagePath(pathname, currentPage + 1)
								}
								variant="tertiaryLink"
								className="no-underline font-semibold"
								data-test-id={PAGINATION_TEST_IDS.PAGINATION_NEXT}
								iconRight={<ArrowRight className="size-5" />}
							>
								{t('next')}
							</AppLink>
						)}
					</div>
				</>
			)}
		</div>
	)
}

export default PaginationRenderer
